/**
 * DraggableWidget — Drag-and-drop wrapper for report widgets.
 *
 * Each widget in edit mode is wrapped in this component, making it both
 * a drag source (via a small handle icon) and a drop target.
 *
 * DROP ZONES:
 *   Cursor position on the target widget determines the drop behavior:
 *   - Top 30px    → horizontal indicator → creates a NEW ROW above
 *   - Bottom 30px → horizontal indicator → creates a NEW ROW below
 *   - Left half   → vertical indicator   → inserts into the SAME ROW (before)
 *   - Right half  → vertical indicator   → inserts into the SAME ROW (after)
 *
 *   Same-row drops onto rows with 6+ widgets are blocked with a toast.
 *
 * ROW DETECTION:
 *   Rows are derived from gridColumn values in the config array.
 *   Any widget whose gridColumn starts at column 1 begins a new row.
 *   All subsequent widgets until the next "column 1" widget belong to that row.
 *
 * AFTER A DROP:
 *   1. The dragged widget is removed from its source row.
 *   2. It's inserted at the target position (same row or new row).
 *   3. All affected rows are redistributed evenly across the 12-column grid.
 *
 * DRAG GHOST:
 *   The browser captures a snapshot of the DOM element for the drag preview.
 *   Elements marked with `data-dnd-hide` (e.g. the "+" AddWidgetButton) are
 *   hidden via `display:none` in the `item()` callback BEFORE the snapshot
 *   is taken, so they don't appear in the ghost. They're restored in `end()`.
 */

import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDrag, useDrop } from "react-dnd";
import { colors2, UI_CAPTION, UI_CAPTION_SEMIBOLD_DF } from "oolib";
import { _getStartEndColumns } from "../../utils/_getStartEndColumns";
import { TOTAL_COLUMNS, GRID_GAP, MAX_WIDGETS_PER_ROW } from "../../constants";
import { useReportConfig } from "../../ReportLandingContext";
import { StyledDropIndicator } from "../../styled";
import { _genHash } from "../../utils/_genHash";
import { GapAddButton } from "../GapAddButton";

const WIDGET_DND_TYPE = "REPORT_WIDGET";

/**
 * A simple fixed-position toast rendered via portal.
 * Used to show "row full" feedback when trying to drop into a 6-widget row.
 */
const MiniToast = ({ message }) => createPortal(
  <div style={{
    position: "fixed",
    top: 20,
    left: "50%",
    transform: "translateX(-50%)",
    background: colors2.red,
    padding: "0.6rem 1.2rem",
    borderRadius: 6,
    zIndex: 99999,
    pointerEvents: "none",
    color: colors2.white
  }}>
    <UI_CAPTION_SEMIBOLD_DF>{message}</UI_CAPTION_SEMIBOLD_DF>
  </div>,
  document.body
);

// ─── Row Detection & Redistribution ──────────────────────────────────────────

/**
 * Walk the config array and group widget indexes into rows.
 * A widget with gridColumn starting at column 1 begins a new row.
 *
 * Example: config with gridColumns ["1/7", "7/13", "1/13"]
 *   → returns [[0, 1], [2]]  (two rows: first has 2 widgets, second has 1)
 */
const getRowsFromConfig = (config) => {
  const rows = [];
  config.forEach((widget, idx) => {
    const [startCol] = _getStartEndColumns(widget.gridColumn);
    if (startCol === 1) {
      rows.push([idx]);
    } else if (rows.length > 0) {
      rows[rows.length - 1].push(idx);
    }
  });
  return rows;
};

/**
 * Evenly distribute gridColumn values for widgets at the given indexes.
 * Each widget gets an equal share of the 12-column grid.
 * Any remainder columns go to the last widget.
 *
 * Example: 3 widgets → "1/5", "5/9", "9/13"
 */
const redistributeRow = (config, rowIndexes) => {
  const total = rowIndexes.length;
  if (total === 0) return;
  const spacePerWidget = Math.floor(TOTAL_COLUMNS / total);
  const spaceForLast = TOTAL_COLUMNS - (total - 1) * spacePerWidget;

  rowIndexes.forEach((configIdx, i) => {
    const startCol = 1 + spacePerWidget * i;
    const isLast = i === total - 1;
    const endCol = startCol + (isLast ? spaceForLast : spacePerWidget);
    config[configIdx].gridColumn = `${startCol} / ${endCol}`;
  });
};

/**
 * Shared helper used by both handleRepositionWidget and handleMoveToNewRow.
 * Takes the manipulated rows array (with -1 as placeholder for the moved widget),
 * filters out empty rows, rebuilds a flat config array, and redistributes each row.
 */
const rebuildConfigFromRows = (rows, cloned, sourceWidget) => {
  const activeRows = rows.filter((row) => row.length > 0);
  const newConfig = [];
  const newRows = [];
  activeRows.forEach((row) => {
    const rowStart = newConfig.length;
    row.forEach((idx) => {
      // -1 is used as a placeholder for the source widget being moved
      newConfig.push(idx === -1 ? sourceWidget : cloned[idx]);
    });
    newRows.push(
      Array.from({ length: row.length }, (_, i) => rowStart + i)
    );
  });
  newRows.forEach((rowIndexes) => redistributeRow(newConfig, rowIndexes));
  return newConfig;
};

// ─── Drop Handlers ───────────────────────────────────────────────────────────

/**
 * Handle dropping a widget into an EXISTING row (left/right drop).
 * Removes the widget from its source row, inserts it before/after the target
 * widget in the target row, then redistributes both rows.
 */
const handleRepositionWidget = ({
  sourceIdx,
  targetIdx,
  dropSide, // "left" or "right"
  config,
  setConfig,
}) => {
  if (sourceIdx === targetIdx) return;

  const cloned = JSON.parse(JSON.stringify(config));
  const rows = getRowsFromConfig(cloned);
  const sourceRowIdx = rows.findIndex((row) => row.includes(sourceIdx));
  const targetRowIdx = rows.findIndex((row) => row.includes(targetIdx));
  const sourceWidget = cloned[sourceIdx];

  // Remove from source row
  rows[sourceRowIdx] = rows[sourceRowIdx].filter((idx) => idx !== sourceIdx);

  // Insert into target row at the correct position
  const targetPosInRow = rows[targetRowIdx].indexOf(targetIdx);
  const insertPos = dropSide === "right" ? targetPosInRow + 1 : targetPosInRow;
  rows[targetRowIdx].splice(insertPos, 0, -1); // -1 = placeholder

  setConfig(rebuildConfigFromRows(rows, cloned, sourceWidget));
};

/**
 * Handle dropping a widget as a NEW ROW (top/bottom drop).
 * Removes the widget from its source row and inserts it as a full-width
 * widget in a brand new row above or below the target widget's row.
 */
const handleMoveToNewRow = ({
  sourceIdx,
  targetIdx,
  dropSide, // "top" or "bottom"
  config,
  setConfig,
}) => {
  if (sourceIdx === targetIdx) return;

  const cloned = JSON.parse(JSON.stringify(config));
  const rows = getRowsFromConfig(cloned);
  const sourceRowIdx = rows.findIndex((row) => row.includes(sourceIdx));
  const targetRowIdx = rows.findIndex((row) => row.includes(targetIdx));
  const sourceWidget = cloned[sourceIdx];

  // Remove from source row
  rows[sourceRowIdx] = rows[sourceRowIdx].filter((idx) => idx !== sourceIdx);

  // Insert as a new single-widget row above or below the target row
  const newRowPosition = dropSide === "top" ? targetRowIdx : targetRowIdx + 1;
  rows.splice(newRowPosition, 0, [-1]); // -1 = placeholder

  // rebuildConfigFromRows will redistribute → single widget gets full width (1/13)
  setConfig(rebuildConfigFromRows(rows, cloned, sourceWidget));
};

// ─── Component ───────────────────────────────────────────────────────────────

export const DraggableWidget = ({ widgetIdx, widgetConfig, setUnderEditWidgetId, children }) => {
  const { config, setConfig } = useReportConfig();
  const ref = useRef(null);
  const dragHandleRef = useRef(null);
  const [dropSide, setDropSide] = useState(null);

  // ─── Gap "+" button logic ────────────────────────────────────────────
  // Determine which edges of this widget should show gap add buttons.
  const rows = getRowsFromConfig(config);
  const myRow = rows.find(row => row.includes(widgetIdx));
  const myRowIdx = rows.findIndex(row => row.includes(widgetIdx));
  const posInRow = myRow ? myRow.indexOf(widgetIdx) : -1;

  // Show "+" in vertical gap (right edge) if there's a widget to the right
  // AND the row hasn't hit the max widget limit
  const showRightGap = myRow && posInRow < myRow.length - 1 && myRow.length < MAX_WIDGETS_PER_ROW;
  // Show "+" in horizontal gap (top edge) for all widgets in non-first rows
  const showTopGap = myRowIdx > 0;

  /**
   * Insert a new widget after this widget in the SAME ROW.
   * Uses -1 placeholder + rebuildConfigFromRows to redistribute evenly.
   */
  const handleInsertInRow = (newWidgetBaseConf) => {
    const cloned = JSON.parse(JSON.stringify(config));
    const clonedRows = getRowsFromConfig(cloned);
    const rowIdx = clonedRows.findIndex(r => r.includes(widgetIdx));
    const pos = clonedRows[rowIdx].indexOf(widgetIdx);

    const newWidget = {
      id: _genHash(4),
      comp: newWidgetBaseConf.comp,
      props: { ...(newWidgetBaseConf.props || {}) },
    };

    clonedRows[rowIdx].splice(pos + 1, 0, -1);
    const newConfig = rebuildConfigFromRows(clonedRows, cloned, newWidget);
    setConfig(newConfig);

    if (setUnderEditWidgetId) setUnderEditWidgetId(newWidget.id);
  };

  /**
   * Insert a new widget as a NEW full-width ROW above this widget's row.
   */
  const handleInsertAsNewRow = (newWidgetBaseConf) => {
    const cloned = JSON.parse(JSON.stringify(config));
    const clonedRows = getRowsFromConfig(cloned);

    const newWidget = {
      id: _genHash(4),
      comp: newWidgetBaseConf.comp,
      props: { ...(newWidgetBaseConf.props || {}) },
    };

    clonedRows.splice(myRowIdx, 0, [-1]);
    const newConfig = rebuildConfigFromRows(clonedRows, cloned, newWidget);
    setConfig(newConfig);

    if (setUnderEditWidgetId) setUnderEditWidgetId(newWidget.id);
  };

  // useDrag: makes this widget draggable.
  //   - `drag` ref goes on the handle (small circle icon in widget corner)
  //   - `dragPreview` ref goes on the wrapper div (what the browser shows as ghost)
  //   - `item()` runs synchronously at drag start, BEFORE the browser captures
  //     the ghost image — this is why we can hide elements there.
  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: WIDGET_DND_TYPE,
    item: () => {
      // Hide elements marked data-dnd-hide before the browser captures the drag ghost.
      // This runs synchronously before the snapshot, so the hidden elements
      // won't appear in the ghost image. Restored in end().
      ref.current?.querySelectorAll("[data-dnd-hide]").forEach(el => { el.style.display = "none"; });
      return { index: widgetIdx, id: widgetConfig.id };
    },
    end: () => {
      // Restore hidden elements after drag finishes
      ref.current?.querySelectorAll("[data-dnd-hide]").forEach(el => { el.style.display = ""; });
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  // useDrop: makes this widget a drop target for other widgets.
  //   - hover() fires continuously while another widget is dragged over this one.
  //     It determines the drop zone (top/bottom/left/right/blocked) based on
  //     cursor position and sets `dropSide` state for visual feedback.
  //   - drop() fires once when the user releases — it calls the appropriate
  //     handler based on dropSide.
  const [{ isOver, canDrop: canDropResult }, drop] = useDrop({
    accept: WIDGET_DND_TYPE,
    canDrop: (item) => {
      // Always return true here — row capacity is checked in hover() for
      // left/right drops only. Top/bottom (new row) drops are always allowed.
      return true;
    },
    hover: (item, monitor) => {
      if (!ref.current || item.index === widgetIdx) {
        setDropSide(null);
        return;
      }
      const rect = ref.current.getBoundingClientRect();
      const offset = monitor.getClientOffset();
      if (!offset) return;

      // Determine which zone the cursor is in:
      //   Top 30px    → "top"    (new row above)
      //   Bottom 30px → "bottom" (new row below)
      //   Middle area → "left" or "right" (same row), or "blocked" if row is full
      const y = offset.y - rect.top;
      const EDGE_ZONE = 30;

      if (y < EDGE_ZONE) {
        setDropSide("top");
      } else if (y > rect.height - EDGE_ZONE) {
        setDropSide("bottom");
      } else {
        // Left/right same-row drop — check row capacity first
        const rows = getRowsFromConfig(config);
        const sourceRow = rows.find((row) => row.includes(item.index));
        const targetRow = rows.find((row) => row.includes(widgetIdx));
        const sameRow = sourceRow === targetRow;
        if (!sameRow && targetRow.length >= MAX_WIDGETS_PER_ROW) {
          setDropSide("blocked"); // triggers MiniToast
          return;
        }
        const midX = rect.left + rect.width / 2;
        setDropSide(offset.x < midX ? "left" : "right");
      }
    },
    drop: (item) => {
      if (item.index === widgetIdx || !dropSide || dropSide === "blocked") return;

      // Route to the appropriate handler based on drop zone
      const handler = (dropSide === "top" || dropSide === "bottom")
        ? handleMoveToNewRow
        : handleRepositionWidget;

      handler({
        sourceIdx: item.index,
        targetIdx: widgetIdx,
        dropSide,
        config,
        setConfig,
      });
      setDropSide(null);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  // Wire up refs:
  //   - drag(dragHandleRef) → only the small handle icon initiates dragging
  //   - drop(dragPreview(ref)) → the whole wrapper is both the drop target
  //     AND the drag preview (what the browser shows as the ghost image)
  drag(dragHandleRef);
  drop(dragPreview(ref));

  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        gridColumn: widgetConfig.gridColumn,
        opacity: isDragging ? 0.4 : 1,
        transition: "opacity 0.2s",
      }}
    >
      {/* Toast shown when trying to drop into a row that already has MAX_WIDGETS_PER_ROW */}
      {isOver && dropSide === "blocked" && <MiniToast message={`Maximum ${MAX_WIDGETS_PER_ROW} Widgets Per Row!`} />}

      {/* Visual drop indicator line — vertical for left/right, horizontal for top/bottom */}
      {isOver && dropSide && dropSide !== "blocked" && <StyledDropIndicator side={dropSide} $gridGap={GRID_GAP} />}

      {/* Gap "+" buttons — appear on hover in the CSS grid gaps between widgets */}
      {showRightGap && (
        <GapAddButton position="right" gridGap={GRID_GAP} onSelect={handleInsertInRow} />
      )}
      {showTopGap && (
        <GapAddButton position="top" gridGap={GRID_GAP} onSelect={handleInsertAsNewRow} />
      )}

      {/*
        Pass dragHandleRef and isDragging down to child components (WidgetRenderer).
        We use cloneElement because DraggableWidget wraps WidgetRenderer as `children`
        (see ReportLanding/index.js), and we need to inject these props without
        WidgetRenderer knowing it's wrapped. This avoids tight coupling.
        - dragHandleRef: WidgetRenderer passes this to DataVizWidgetShell, which
          attaches it to the small drag handle icon in the widget's corner.
        - isDragging: used to hide the AddWidgetButton (+) during drag, since
          React state changes happen after the ghost snapshot.
      */}
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, { dragHandleRef, isDragging })
          : child
      )}
    </div>
  );
};
